/**
 * BatchAccessField
 *
 * Reusable admin form control for setting Batch-Based Access Control on
 * recordings, modules, and notes. Renders:
 *   • Visibility select (Public / All students / Selected batches only / Admin only)
 *   • Multi-select checkbox list of active batches (shown only when
 *     visibility === "selected_batches")
 *
 * Emits changes via `onChange({ visibility, allowed_batches })` — parent owns
 * the form state so the same field works cleanly across every content type.
 *
 * Props:
 *   visibility        (str)    current value: public|all_students|selected_batches|admin_only
 *   allowedBatches    (str[])  currently-selected batch names
 *   batches           (obj[])  from `/api/admin/batches` — { id, name, status, ... }
 *   onChange          (fn)     called with { visibility, allowed_batches }
 *   contentLabel      (str)    e.g. "recording", "module", "note" — used in help text
 *   testId            (str)    optional data-testid prefix
 */
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Globe, Users, Lock } from "lucide-react";

const VISIBILITY_OPTIONS = [
    { value: "public",           label: "Public preview",        icon: Globe,  desc: "Visible without login (marketing / preview)" },
    { value: "all_students",     label: "All registered students", icon: Users,  desc: "Visible to every logged-in student (default)" },
    { value: "selected_batches", label: "Selected batches only", icon: Shield, desc: "Only students in the batches you pick below" },
    { value: "admin_only",       label: "Admin only",            icon: Lock,   desc: "Draft — not visible to any student" },
];

export function BatchAccessField({
    visibility = "all_students",
    allowedBatches = [],
    batches = [],
    onChange,
    contentLabel = "content",
    testId = "batch-access",
}) {
    const active = (visibility || "all_students").toLowerCase();
    const activeOption = VISIBILITY_OPTIONS.find((o) => o.value === active) || VISIBILITY_OPTIONS[1];
    const Icon = activeOption.icon;

    const setVisibility = (v) => onChange?.({ visibility: v, allowed_batches: allowedBatches });
    const toggleBatch = (name, checked) => {
        const next = checked
            ? Array.from(new Set([...(allowedBatches || []), name]))
            : (allowedBatches || []).filter((b) => b !== name);
        onChange?.({ visibility: active, allowed_batches: next });
    };

    const activeBatches = (batches || []).filter((b) => (b.status || "active") !== "archived");
    const selectedCount = (allowedBatches || []).length;

    return (
        <div className="rounded-xl border border-border/60 bg-muted/20 p-4 space-y-3" data-testid={testId}>
            <div className="flex items-center gap-2">
                <div className="icon-tile icon-tile-teal h-8 w-8">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">Batch Access</p>
                    <p className="text-sm font-medium mt-0.5">Who can view this {contentLabel}?</p>
                </div>
            </div>

            <div className="space-y-1.5">
                <Label className="text-xs">Visibility</Label>
                <Select value={active} onValueChange={setVisibility}>
                    <SelectTrigger data-testid={`${testId}-visibility`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {VISIBILITY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                <div className="flex items-center gap-2">
                                    <opt.icon className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span>{opt.label}</span>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">{activeOption.desc}</p>
            </div>

            {active === "selected_batches" && (
                <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between">
                        <Label className="text-xs">Allowed batches</Label>
                        <span className="text-[11px] text-muted-foreground">{selectedCount} selected</span>
                    </div>
                    {activeBatches.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic px-3 py-2 rounded-md bg-muted/40 border border-border/50">
                            No active batches available. Create one under Batches first.
                        </p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                            {activeBatches.map((b) => {
                                const checked = (allowedBatches || []).includes(b.name);
                                return (
                                    <label
                                        key={b.id}
                                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-all ${
                                            checked
                                                ? "border-primary/50 bg-primary/8"
                                                : "border-border/60 hover:border-border bg-background/40"
                                        }`}
                                        data-testid={`${testId}-batch-${b.name.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onCheckedChange={(v) => toggleBatch(b.name, !!v)}
                                        />
                                        <span className="text-sm font-medium">{b.name}</span>
                                        {b.status && b.status !== "active" && (
                                            <span className="text-[10px] uppercase text-muted-foreground ml-auto">{b.status}</span>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    )}
                    {selectedCount === 0 && (
                        <p className="text-[11px] text-amber-500">
                            No batches selected — the {contentLabel} will fall back to "All registered students".
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
